import Vue from 'vue';
import { Notification } from 'element-ui';
import {
  LOGIN_REQUEST,
  LOGIN_SUCCESS,
  LOGIN_FAILURE,
} from '@/constants/types';
import { HTTP } from '@/store/service';
import IAM_API from '@/store/iamService';
import * as SSO from '@/util/singleSignOn';

const InitialState = {
  isAuthenticated: false,
  access_token: null,
  refresh_token: null,
  maintenance_mode: false,
  [`${LOGIN_REQUEST}`]: false,
  [`${LOGIN_SUCCESS}`]: false,
  [`${LOGIN_FAILURE}`]: false,
};

const mutations = {
  update(state, current) {
    return Object.assign(state, current);
  },
  [`${LOGIN_REQUEST}`](state, status) {
    Object.assign(state, { [`${LOGIN_REQUEST}`]: status });
  },
  [`${LOGIN_FAILURE}`](state, error) {
    Object.assign(state, { [`${LOGIN_FAILURE}`]: error });
  },
  [`${LOGIN_SUCCESS}`](state, response) {
    Object.assign(state, { [`${LOGIN_SUCCESS}`]: response });
  },
};

const actions = {
  async login({ commit, dispatch }, payload) {
    try {
      commit(LOGIN_REQUEST, true);
      const { email, password } = payload;
      const method = 'post';
      const url = 'auth/token';
      const data = {
        token: {
          grant_type: 'user_credentials',
          email,
          password,
        },
      };

      const response = await HTTP({ method, url, data });
      const isSuccess = response.status === 200 || response.status === 201;
      const isMaintenance = response.status === 503;
      if (isSuccess) {
        // Login into the IAM
        await dispatch('iamLogin', payload);

        // eslint-disable-next-line
        HTTP.defaults.headers.common['Authorization'] = `Bearer ${response.data.data.access_token}`;
        const domainResponse = await HTTP({
          method: 'get', url: 'auth/domains', data: {},
        });
        const origin = window.location.origin;
        const domainList = domainResponse.data.data;
        if (domainList && domainList.length > 0) {
          const urlList = domainList.map((el) => el.platform_url);
          if (urlList.includes(origin)) {
            const current = {
              isAuthenticated: isSuccess,
              access_token: response.data.data.access_token,
              refresh_token: response.data.data.refresh_token,
              maintenance_mode: isMaintenance,
            };
            commit('update', current);
            commit(LOGIN_REQUEST, false);
            commit(LOGIN_SUCCESS, isSuccess);
            Notification({
              type: 'success',
              title: 'Success!',
              message: 'You are successfully logged in',
            });
            return response.data.data;
          }
          window.location.href = urlList[0];
        } else window.location.href = process.env.PLATFORM_URL;
      }
      return {};
    } catch (error) {
      commit(LOGIN_SUCCESS, false);
      commit(LOGIN_REQUEST, false);
      commit(LOGIN_FAILURE, true);
      if (error.response
        && error.response.status
        && error.response.status === 503) {
        commit('update', { maintenance_mode: true });
        window.location.href = '/maintenance';
      } else commit('update', { maintenance_mode: false });
      Vue.config.errorHandler(error);
      return {};
    }
  },
  async refreshToken({ commit }, payload) {
    try {
      const method = 'post';
      const url = 'auth/token';
      const data = {
        token: {
          grant_type: 'refresh_token',
          refresh_token: payload.refresh_token,
        },
      };
      // eslint-disable-next-line
      delete HTTP.defaults.headers.common['Authorization'];
      const response = await HTTP({ method, url, data });
      const isSuccess = response.status === 200 || response.status === 201;
      const isMaintenance = response.status === 503;
      const current = {
        isAuthenticated: isSuccess,
        access_token: response.data.data.access_token,
        refresh_token: response.data.data.refresh_token,
        maintenance_mode: isMaintenance,
      };
      // eslint-disable-next-line
      HTTP.defaults.headers.common['Authorization'] = `Bearer ${response.data.data.access_token}`;
      commit('update', current);
    } catch (e) {
      console.log(e);
      throw e;
    }
  },
  SOCKET_connect() {
  },
  SOCKET_notification({ commit }, message) {
    if (message.operation === 'MAINTENANCE_OFF') {
      commit('update', { maintenance_mode: false });
      if (window.location.pathname === '/maintenance') window.location.href = '/';
    }
    if (message.operation === 'MAINTENANCE_ON') {
      commit('update', { maintenance_mode: true });
      window.location.href = '/maintenance';
    }
  },
  SOCKET_authenticated() {
  },

  /** Login into Sequire IAM */
  async iamLogin(store, payload) {
    try {
      // Make the request to the IAM
      const { email, password } = payload;
      const response = await IAM_API.post('auth/token', {
        token: {
          grant_type: 'user_credentials',
          email,
          password,
        },
      });

      // If successful, save the tokens to the shared storage
      const isSuccess = (response.status === 200 || response.status === 201);
      if (isSuccess) {
        // Create the payload
        const responseData = response.data.data;
        const authData = {
          isAuthenticated: isSuccess,
          access_token: responseData.access_token,
          refresh_token: responseData.refresh_token,
          applications: responseData.applications,
          user_id: responseData.user_id,
          first_name: responseData.first_name,
          last_name: responseData.last_name,
        };

        await SSO.updateAuthData(authData);
      }

      return Promise.resolve(isSuccess);
    } catch (error) {
      console.error(error);
      return Promise.resolve(false);
    }
  },
};

const getters = {
  auth: (state) => state.isAuthenticated,
  access_token: (state) => state.access_token,
  refresh_token: (state) => state.refresh_token,
};

export default {
  state: InitialState,
  mutations,
  actions,
  getters,
  namespaced: true,
};
